import { useContext, useEffect, useMemo, useState } from "react";
import { DeferHiddenBlocksContext } from "@/components/deferred-mount/DeferredMount";
import { RankedPanel, initialRanking, panelOrder, parseRanking } from "./utils";

/**
 * One key for both regions, on purpose: a signed-in reader's region arrives from their settings
 * after the panels have mounted (the page starts on US & Canada), so a key per region would be
 * read for the region the page starts on and written for the one it ends on, and a Europe
 * reader would never get their own ranking back. The last ranking stored is the region the
 * reader ended on, which is where their next visit ends up too.
 */
const STORAGE_KEY = "markets-pulse-panel-order";

const readRanking = () => {
    try {
        return parseRanking(window.localStorage.getItem(STORAGE_KEY));
    } catch {
        return null;
    }
};

/**
 * The lower panels' CSS `order`, fixed for as long as they are mounted (see `initialRanking`).
 * Today's ranking, when it arrives later, is kept for the reader's next visit.
 *
 * The order also holds when the region changes while the panels are on screen. For signed-in
 * Europe readers that change is their settings arriving just after the page has drawn, and
 * re-ranking then moved the panels sideways, the layout shift this exists to remove. Every panel's
 * content reloads for the new region anyway; its ranking is used from the next visit.
 *
 * The stored ranking is read in the first render, which pre-rendered HTML could not match: it is
 * only read in a browser's render, where the lower panels mount after hydration (`WhenNear`). In a
 * crawler's full render they are in the server HTML, and take the fixed order on both sides.
 */
export const usePanelOrder = (today: RankedPanel[] | null) => {
    const browserRender = useContext(DeferHiddenBlocksContext);
    const [ranking] = useState(() => initialRanking(today, browserRender ? readRanking() : null));

    useEffect(() => {
        if (!today) return;
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(today));
        } catch {
            // Private mode or storage full: the next visit starts from the fixed order.
        }
    }, [today]);

    return useMemo(() => panelOrder(ranking), [ranking]);
};
